// The price slider (web/index.html #price-pop, web/controls.js): "Price" / "Up to $10", a slider up to nowish.json
// price.ceiling, a hint, and Reset while a price is set. It closes by itself price.autoCloseMs after you let go of
// it (or after the last arrow key); touching anything in it first waits again.
import { useEffect, useRef, type RefObject } from "react";
import { SHARED, T } from "../../shared/config";
import { RULES } from "../../shared/rules";
import { setFreePrice, setPopover, useView } from "../../state/view";
import { Popover } from "../../ui/Popover";
import { ResetPill } from "./FilterChip";
import styles from "./FilterBar.module.css";

const CEILING = SHARED.price.ceiling;
export const priceLabel = (max: number | null) => (max != null ? T("filter.upTo", { price: RULES.dollars(max) }) : T("filter.anyPrice"));
const KEYS = ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End", "PageUp", "PageDown"];

export function PricePopover({ anchor, leaving, onGone, onAutoClose }: {
  anchor: RefObject<HTMLButtonElement | null>; leaving: boolean; onGone: () => void; onAutoClose: () => void;
}) {
  const max = useView((s) => s.filter.maxPrice);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null), grabbing = useRef(false);
  const cancel = () => { if (timer.current) { clearTimeout(timer.current); timer.current = null; } };
  const schedule = () => { cancel(); timer.current = setTimeout(() => { timer.current = null; onAutoClose(); }, SHARED.price.autoCloseMs); };
  useEffect(() => {
    const up = () => { if (grabbing.current) { grabbing.current = false; schedule(); } };
    addEventListener("pointerup", up);
    addEventListener("pointercancel", up);
    return () => { removeEventListener("pointerup", up); removeEventListener("pointercancel", up); cancel(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Popover anchor={anchor} id="price-pop" leaving={leaving} onGone={onGone} label={T("filter.anyPrice")}
      onPointerDown={(ev) => { cancel(); grabbing.current = (ev.target as Element).id === "price-range"; }}>
      <div id="price-label" className={styles.title}>{priceLabel(max)}</div>
      <input id="price-range" type="range" min={0} max={CEILING} step={SHARED.price.step} aria-label="Max price"
        className={styles.range} value={max ?? CEILING}
        onChange={(ev) => { const v = Number(ev.target.value); setFreePrice({ maxPrice: v >= CEILING ? null : v }); }}
        onKeyDown={(ev) => { if (KEYS.includes(ev.key)) schedule(); }} />
      <div className={styles.foot}>
        <div className="muted small">{T("filter.priceHint")}</div>
        <ResetPill id="price-reset" shown={max != null} onClick={() => { cancel(); setFreePrice({ maxPrice: null }); setPopover(null); }} />
      </div>
    </Popover>
  );
}
